import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { CoreTracking } from "../../../../models/CoreTracking";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalComponent from '../../../misc/ModalComponent';
import { ClearMapAsmUserTracking, MapAsmUserTracking } from '../../../../store/TrackingOperationAction';
import { RequestState } from '../../../../models/RequestState';
import { CoreAsmUserTrackingConflict, CoreAsmUserTrackingMapping } from '../../../../models/CoreTrackingOperation';
import StatusCode from '../../../../util/StatusCode';
import Loading from '../../../misc/Loading';
import AsmUserTrackingOperationsValidationResult from "./AsmUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../store/store";



interface MapAsmUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const MapAsmUserTrackingWizard: React.FC<MapAsmUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "schoolAdministration"]);
    const [wizardStep, setWizardStep] = useState<'input' | 'verify' | 'done'>('input');
    const [update, setUpdate] = useState<CoreAsmUserTrackingMapping>({ asmId: '' });
    const mapAsmUserTracking = useSelector<RootState, RequestState<CoreAsmUserTrackingConflict[]>>(state => state.trackingOperation.mapAsmUserTracking)
    const initial: CoreAsmUserTrackingMapping = {
        asmId: props.tracking.sourceSystemId
    };

    const updateAsmId: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            asmId: e.target.value,
        });
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapAsmUserTracking(update, props.tracking.trackingId, true)(dispatch);
        setWizardStep('verify');
    }

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapAsmUserTracking(update, props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        ClearMapAsmUserTracking()(dispatch);
        setWizardStep('input');
        setUpdate({ asmId: '' });
        props.close(success);
    }

    switch (wizardStep) {
        case 'input': {
            if (isCurrentlyLifecycleManaged(props.tracking)) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}><FontAwesomeIcon title="ERROR" color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.asm.map.currenlyInGraceError', { ns: 'schoolAdministration' })}</ModalComponent>;
            }

            return <ModalComponent
                isOpen={true}
                toggleModal={() => close(false)}
                header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                update={submitInput}
                disabled={update.asmId === ''}
                confirm={t('action.validate', { ns: 'common' })}
                cancel={t('action.cancel', { ns: 'common' })}>
                <Form>
                    <FormGroup key="asmAccountId">
                        <Label for="asmAccountId">{t('trackingOperations.asm.map.accountIdLabel', { ns: 'schoolAdministration' })}</Label>
                        <Input
                            type="text"
                            name="asmAccountId"
                            id="asmAccountId"
                            placeholder={t('trackingOperations.asm.map.accountIdPlaceholder', { ns: 'schoolAdministration' })}
                            autoComplete="off"
                            invalid={update.asmId === ''}
                            value={update.asmId}
                            onChange={updateAsmId} />
                        <FormFeedback>{t('trackingOperations.asm.map.accountIdMissingFeedback', { ns: 'schoolAdministration' })}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>;
        }
        case 'verify': {
            if (mapAsmUserTracking.code === StatusCode.COMPLETE) {
                if (update.asmId === initial.asmId) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                        cancel={t('action.cancel', { ns: 'common' })}>{t('trackingOperations.asm.map.noChangeError', { ns: 'schoolAdministration' })}</ModalComponent>
                } else if (mapAsmUserTracking.data.length === 0) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                        update={confirmInput}
                        confirm={t('action.confirm', { ns: 'common' })}
                        cancel={t('action.cancel', { ns: 'common' })}>
                        <p><FontAwesomeIcon title="OK" color="green" icon={['fas', 'check-circle']} /> {t('trackingOperations.asm.map.noIssuesText', { ns: 'schoolAdministration' })}</p>
                        {(update.asmId !== initial.asmId) && <div className="mt-3">
                            <b>{t('trackingOperations.asm.map.reviewTitle', { ns: 'schoolAdministration' })}</b>
                            <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                <div>{t('trackingOperations.asm.map.reviewFrom', { ns: 'schoolAdministration' })}</div>
                                <div>{initial.asmId}</div>
                                <div>{t('trackingOperations.asm.map.reviewTo', { ns: 'schoolAdministration' })}</div>
                                <div>{update.asmId}</div>
                            </div>
                        </div>}
                        <p className="mt-3"><FontAwesomeIcon title="WARNING" color="#fc1" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.asm.map.currentWillDeactivateMessage', { ns: 'schoolAdministration' })}</p>
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <p><FontAwesomeIcon title="ERROR" color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.asm.map.conflictsDetected', { ns: 'schoolAdministration' })}</p>
                    <AsmUserTrackingOperationsValidationResult asmId={update.asmId} conflicts={mapAsmUserTracking.data} />
                </ModalComponent>;
            } else if (mapAsmUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}><CoreApiError  requestState={mapAsmUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (mapAsmUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.close', { ns: 'common' })}>{t('trackingOperations.asm.map.complete', { ns: 'schoolAdministration' })}</ModalComponent>
            } else if (mapAsmUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}><CoreApiError requestState={mapAsmUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.asm.map.title', { ns: 'schoolAdministration' })}
                    cancel={t('action.cancel', { ns: 'common' })}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

const isCurrentlyLifecycleManaged = (trackig: CoreTracking) => trackig.active && !trackig.deleted && (trackig.inactivated || trackig.archived);

export default MapAsmUserTrackingWizard;
